const API_BASE_URL =
  import.meta.env.VITE_API_URL || "http://localhost:8080";

// Default timeout for most API calls (10 seconds)
const DEFAULT_TIMEOUT_MS = 10_000;

// Extended timeout for LLM-evaluated operations (2 minutes for complex HLD evaluations)
export const LLM_TIMEOUT_MS = 120_000;

export { API_BASE_URL };

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export class TimeoutError extends Error {
  constructor(timeoutMs: number) {
    super(`Request timed out after ${timeoutMs}ms`);
    this.name = "TimeoutError";
  }
}

// Track in-flight refresh to prevent race conditions when multiple
// requests fail with 401 simultaneously
let refreshPromise: Promise<boolean> | null = null;

async function tryRefreshToken(): Promise<boolean> {
  // If a refresh is already in progress, wait for it
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
        method: "POST",
        credentials: "include",
      });
      return response.ok;
    } catch {
      return false;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

export interface ApiFetchOptions extends RequestInit {
  /** Timeout in milliseconds. Use LLM_TIMEOUT_MS for LLM-evaluated operations. */
  timeout?: number;
}

export async function apiFetch<T>(
  endpoint: string,
  options: ApiFetchOptions = {}
): Promise<T> {
  const { timeout = DEFAULT_TIMEOUT_MS, ...restOptions } = options;

  // Create AbortController for timeout
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  const fetchOptions: RequestInit = {
    ...restOptions,
    credentials: "include",
    signal: controller.signal,
    headers: {
      "Content-Type": "application/json",
      ...restOptions.headers,
    },
  };

  try {
    let response = await fetch(`${API_BASE_URL}${endpoint}`, fetchOptions);

    // If 401 and not already trying to refresh, attempt token refresh
    if (response.status === 401 && !endpoint.includes("/auth/refresh")) {
      const refreshed = await tryRefreshToken();
      if (refreshed) {
        // Retry the original request
        response = await fetch(`${API_BASE_URL}${endpoint}`, fetchOptions);
      }
    }

    if (!response.ok) {
      const errorText = await response.text();
      throw new ApiError(
        response.status,
        errorText || `API Error: ${response.status}`
      );
    }

    // Safely parse JSON response, handling empty bodies and invalid JSON
    const text = await response.text();
    if (!text) {
      return null as T;
    }
    try {
      return JSON.parse(text) as T;
    } catch {
      throw new ApiError(
        response.status,
        `Invalid JSON response: ${text.slice(0, 100)}`
      );
    }
  } catch (error) {
    if (error instanceof Error && error.name === "AbortError") {
      throw new TimeoutError(timeout);
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
}
